import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { LIMIT_FOR_FETCHING_ADMIN_ITEMS } from '~/shared/constants';
import { normalizeParticipants } from '~/shared/helpers/lesson-helpers';
import type { FileType, LessonExerciseType, LessonType } from '~/types';

type CourseCategoryType = {
  _id: string;
  title: string;
  alias?: string | null;
  categoryIndex?: number | null;
};

type LessonsListFilterType = {
  lessonTemplateId: string | null;
  courseAlias: string | null;
  courseCategoryAlias: string | null;
  selectedCourseCategory: CourseCategoryType | null;
} | null;

type PageInfoType = {
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  currentPage: number;
  perPage: number;
  itemCount: number;
  pageCount: number;
};

type PaginationType<TItem> = {
  isLoading: boolean;
  items: TItem[];
  pageInfo: PageInfoType;
};

type PeerUserMetaType = {
  userId?: string;
  name?: string;
  peerId?: string;
  deviceId?: string;
  audio?: boolean;
  video?: boolean;
  myPeer?: boolean;
};

type LessonUserListItemType = {
  peerId: string | null;
  hasStream: boolean;
  isMyScreenSharing: boolean;
  peerUser: PeerUserMetaType;
};

type HomeTaskModalType = {
  isShowed: boolean;
};

type LessonCallStateType = {
  myVideoStream: MediaStream | null;
  myVideoTrack: MediaStreamTrack | null;
  isLessThan10MinutesLeft: boolean;
  selectedZeroLessonId: string | null;
  homeTaskModal: HomeTaskModalType;
  selectedZeroLessonCategoryId: string | null;
  fullScreenUserId: string | null;
  videoDevices: MediaDeviceInfo[];
  editableStudentId: string | null;
  editableHomeTaskLesson: LessonType | null;
  isCloseFullScreen: boolean;
  isPreviewShowed: boolean;
  isMyVideoLoaded: boolean;
  isStartLessonButtonShowed: boolean;
  isShowAddLessonExercisesList: boolean;
  isLessonComplete: boolean;
  myVideoLoading: boolean;
  isRecording: boolean;
  isShowVideoStreams: boolean;
  isInfoModalShowed: boolean;
  isRefetchUsersData: boolean;
  isRefetchUserGroupsData: boolean;
  isUpdateEditableHomeTaskForm: boolean;
  audioMuted: boolean;
  isShowNoCameraAccessAlertBanner: boolean;
  isCallToolsCollapsed: boolean;
  isShowStudentVideoStreams: boolean;
  isAllButtonsMode: boolean;
  isLessonExercisesList: boolean;
  isAccessToCameraMicDenied: boolean;
  isMyPeerShowed: boolean;
  isUsersList: boolean;
  isRefetchLesson: boolean;
  isConnectionEstablished: boolean;
  isMobileDevice: boolean;
  showVideoTools: boolean;
  isShowLessonExercisesList: boolean;
  isMessagesList: boolean;
  showHeader: boolean;
  isCall: boolean;
  zeroLessonDateTime: string | null;
  selectedExerciseIndex: number | null;
  roomId: string | null;
  showExerciseForStudent: boolean;
  videoMuted: boolean;
  isVideoConnectionEstablished: boolean;
  isUpdateStudentLesson: boolean;
  isUnsetSelectedUser: boolean;
  isDateTimeExpired: boolean;
  isFullScreenMode: boolean;
  isLessonEstablished: boolean;
  isSetFullScreen: boolean;
  isScreenSharing: boolean;
  isCloseCall: boolean;
  isShowPromptCameraAccessBanner: boolean;
  isMounting: boolean;
  isLessonLoading: boolean;
  isStudentFullScreenMode: boolean;
  isLessonResults: boolean;
  isCallStarted: boolean;
  isCameraUsedByAnotherApp: boolean;
  fullScreen: string | null;
  isLessonStatusInfo: boolean;
  allowedParticipants: string[];
  isAddExercisesToLesson: boolean;
  isCalling: boolean;
  showVideoDevices: boolean;
  hasMyVideoStream: boolean;
  poorConnection: boolean;
  reconnecting: boolean;
  isUpdate: boolean;
  isSketchMode: boolean;
  isZeroLessonNotComplete: boolean;
  toolsTab: number;
  openedToolsBeforeFullScreen: string | null;
  studentFileSelectionFromTeacher: StudentFileSelectionType | null;
  peerIdToRemove: string | null;
  myVideoTrackId: string | null;
  initialLessonId: string | null;
  deviceId: string | null;
  peerId: string | null;
  isZeroLessonCategoryChosen: boolean;
  studentFileSelection: StudentFileSelectionType | null;
  selectedExerciseWithFiles: FileType | null;
  isDocReaderShowed: boolean;
  isSaveLessonTemplatesMode: boolean;
  lessonUsersList: LessonUserListItemType[];
  controlOwnerUserId: string | null;
  controlPendingFromUserId: string | null;
  isShowStudentLesson: boolean;
};

type LessonsSliceType = {
  isDocReaderShowed: boolean;
  isShowStudentLesson: boolean;
  isBasicModuleStructure: boolean;
  isTemplateLessons: boolean;
  cloneLessonMode: boolean;
  isCourseCategoryComplete: boolean;
  isSuperTeacherMode: boolean;
  lessonTemplateLessonsPaginationReset: boolean;
  isEditableLessons: boolean;
  lesson: LessonType | null;
  zeroLesson: LessonType | null;
  cloneLesson: LessonType | null;
  zeroLessonTemplateLesson: LessonType | null;
  editableLesson: LessonType | null;
  draggedLesson: LessonType | null;
  courseAlias: string | null;
  currentBoardLessonIndex: number | null;
  lessonsListFilter: LessonsListFilterType;
  selectedEventLesson: LessonType | null;
  currentBoardLesson: LessonType | null;
  studentsListTab: number;
  isLessonsPaginationReset: boolean;
  lessonCall: LessonCallStateType;
  lessonsPagination: PaginationType<Record<string, unknown>>;
  lessonTemplateLessonsPagination: PaginationType<Record<string, unknown>>;
};

export const initialPageInfo: PageInfoType = {
  hasPreviousPage: false,
  hasNextPage: false,
  currentPage: 1,
  perPage: LIMIT_FOR_FETCHING_ADMIN_ITEMS,
  itemCount: 0,
  pageCount: 1,
};

export type StudentFileSelectionType = {
  isCurrentFile: boolean;
  currentFileIndex: number;
  lesson: LessonType | null;
  isLessonSet: boolean;
  files?: FileType[];
  exercise?: LessonExerciseType | null;
  fileStatus?: string | null;
  exerciseIndex: number;
};

const initialState: LessonsSliceType = {
  lesson: null,
  editableLesson: null,
  zeroLesson: null,
  studentsListTab: 0,
  isTemplateLessons: false,
  isBasicModuleStructure: false,
  cloneLessonMode: false,
  isEditableLessons: true,
  isSuperTeacherMode: false,
  draggedLesson: null,
  cloneLesson: null,
  isLessonsPaginationReset: false,
  isCourseCategoryComplete: false,
  zeroLessonTemplateLesson: null,
  lessonsListFilter: null,
  currentBoardLesson: null,
  selectedEventLesson: null,
  courseAlias: null,
  currentBoardLessonIndex: null,
  isDocReaderShowed: false,
  isShowStudentLesson: false,
  lessonCall: {
    isLessonLoading: true,
    myVideoStream: null,
    myVideoTrack: null,
    isVideoConnectionEstablished: false,
    hasMyVideoStream: false,
    isLessThan10MinutesLeft: false,
    isAddExercisesToLesson: false,
    homeTaskModal: {
      isShowed: false,
    },
    controlOwnerUserId: null,
    controlPendingFromUserId: null,
    editableHomeTaskLesson: null,
    lessonUsersList: [],
    openedToolsBeforeFullScreen: null,
    peerIdToRemove: null,
    selectedZeroLessonId: null,
    selectedExerciseIndex: null,
    deviceId: null,
    isSaveLessonTemplatesMode: false,
    selectedZeroLessonCategoryId: null,
    editableStudentId: null,
    fullScreen: null,
    isShowVideoStreams: true,
    isStartLessonButtonShowed: false,
    isPreviewShowed: false,
    isDateTimeExpired: false,
    isRefetchLesson: false,
    isRefetchUserGroupsData: false,
    isSetFullScreen: false,
    isShowAddLessonExercisesList: false,
    isCloseCall: false,
    isShowPromptCameraAccessBanner: false,
    isZeroLessonNotComplete: false,
    myVideoLoading: false,
    isRefetchUsersData: false,
    isRecording: false,
    isLessonEstablished: false,
    isCalling: false,
    isMobileDevice: false,
    isMyVideoLoaded: false,
    isMounting: true,
    roomId: null,
    studentFileSelectionFromTeacher: null,
    videoDevices: [],
    isScreenSharing: false,
    isConnectionEstablished: false,
    fullScreenUserId: null,
    initialLessonId: null,
    studentFileSelection: null,
    zeroLessonDateTime: null,
    peerId: null,
    isCallToolsCollapsed: false,
    poorConnection: false,
    reconnecting: false,
    isUpdate: false,
    isCallStarted: false,
    isUpdateStudentLesson: false,
    showVideoDevices: false,
    isStudentFullScreenMode: false,
    isSketchMode: false,
    videoMuted: false,
    isShowNoCameraAccessAlertBanner: false,
    isFullScreenMode: false,
    isLessonStatusInfo: true,
    isCameraUsedByAnotherApp: false,
    audioMuted: true,
    showHeader: true,
    showVideoTools: false,
    isMyPeerShowed: false,
    myVideoTrackId: null,
    isShowStudentVideoStreams: true,
    isAccessToCameraMicDenied: false,
    isShowLessonExercisesList: false,
    isUpdateEditableHomeTaskForm: false,
    isUnsetSelectedUser: false,
    isInfoModalShowed: false,
    isLessonComplete: false,
    isLessonExercisesList: true,
    isAllButtonsMode: false,
    isCall: false,
    isUsersList: true,
    allowedParticipants: [],
    isMessagesList: false,
    toolsTab: 0,
    showExerciseForStudent: false,
    isLessonResults: false,
    isZeroLessonCategoryChosen: false,
    selectedExerciseWithFiles: null,
    isCloseFullScreen: false,
    isDocReaderShowed: true,
    isShowStudentLesson: false,
  },
  lessonsPagination: {
    items: [],
    isLoading: false,
    pageInfo: initialPageInfo,
  },
  lessonTemplateLessonsPaginationReset: false,
  lessonTemplateLessonsPagination: {
    items: [],
    isLoading: false,
    pageInfo: initialPageInfo,
  },
};

const mergePeerMeta = (prev: PeerUserMetaType = {}, next: PeerUserMetaType = {}): PeerUserMetaType => {
  return {
    ...prev,
    ...next,
    name: next.name ?? prev.name,
    deviceId: next.deviceId ?? prev.deviceId,
    audio: typeof next.audio === 'boolean' ? next.audio : prev.audio,
    video: typeof next.video === 'boolean' ? next.video : prev.video,
    peerId: next.peerId ?? prev.peerId,
    userId: next.userId ?? prev.userId,
  };
};

export const lessonsSlice = createSlice({
  name: 'lessons',
  initialState,
  reducers: {
    updatePeerUser: (state, { payload }: PayloadAction<{ peerId?: string | null; peerUser: PeerUserMetaType }>) => {
      const { peerId, peerUser } = payload;
      const list = state.lessonCall.lessonUsersList;

      let index = peerId ? list.findIndex((item) => String(item?.peerId ?? '') === String(peerId)) : -1;

      if (index < 0 && peerUser?.userId) {
        index = list.findIndex((item) => String(item?.peerUser?.userId ?? '') === String(peerUser.userId));
      }

      if (index >= 0) {
        const current = list[index];
        const mergedPeerUser = mergePeerMeta(current?.peerUser, {
          ...peerUser,
          peerId: peerId ?? peerUser?.peerId,
        });

        list[index] = {
          ...current,
          peerId: peerId ?? current.peerId ?? null,
          hasStream: current.hasStream,
          isMyScreenSharing: current.isMyScreenSharing,
          peerUser: mergedPeerUser,
        };
      } else {
        list.push({
          peerId: peerId ?? null,
          hasStream: false,
          isMyScreenSharing: false,
          peerUser: {
            ...peerUser,
            peerId: peerId ?? peerUser?.peerId,
          },
        });
      }

      if (peerUser?.userId) {
        const withPeerIdIndex = list.findIndex(
          (item) => item?.peerId && String(item?.peerUser?.userId ?? '') === String(peerUser.userId),
        );
        const withoutPeerIdIndex = list.findIndex(
          (item) => !item?.peerId && String(item?.peerUser?.userId ?? '') === String(peerUser.userId),
        );

        if (withPeerIdIndex >= 0 && withoutPeerIdIndex >= 0 && withPeerIdIndex !== withoutPeerIdIndex) {
          const first = list[withPeerIdIndex];
          const second = list[withoutPeerIdIndex];

          list[withPeerIdIndex] = {
            ...first,
            hasStream: Boolean(first?.hasStream || second?.hasStream),
            isMyScreenSharing: Boolean(first?.isMyScreenSharing || second?.isMyScreenSharing),
            peerUser: mergePeerMeta(first?.peerUser, second?.peerUser),
          };

          list.splice(withoutPeerIdIndex, 1);
        }
      }

      state.lessonCall.lessonUsersList = list;
    },

    setAllowedParticipants: (state, action: PayloadAction<string[]>) => {
      state.lessonCall.allowedParticipants = normalizeParticipants((action.payload ?? []).map(String));
    },
    clearAllowedParticipants: (state) => {
      state.lessonCall.allowedParticipants = [];
    },

    setCloneLesson: (state, action: PayloadAction<LessonType>) => {
      state.cloneLesson = action.payload;
    },
    setCourseAlias: (state, action: PayloadAction<string | null>) => {
      state.courseAlias = action.payload;
    },
    setSelectedEventLesson: (state, action: PayloadAction<LessonType>) => {
      state.selectedEventLesson = action.payload;
    },
    setCurrentBoardLesson: (state, action: PayloadAction<LessonType>) => {
      state.currentBoardLesson = action.payload;
    },
    setCurrentBoardLessonIndex: (state, action: PayloadAction<number | null>) => {
      state.currentBoardLessonIndex = action.payload;
    },
    setStudentsListTab: (state, action: PayloadAction<number>) => {
      state.studentsListTab = action.payload;
    },
    setCloneLessonMode: (state, action: PayloadAction<boolean>) => {
      state.cloneLessonMode = action.payload;
    },
    setLessonTemplateLessonsPaginationReset: (state, action: PayloadAction<boolean>) => {
      state.lessonTemplateLessonsPaginationReset = action.payload;
    },
    setIsEditableLessons: (state, action: PayloadAction<boolean>) => {
      state.isEditableLessons = action.payload;
    },
    setLessonsListFilter: (state, action: PayloadAction<LessonsListFilterType>) => {
      state.lessonsListFilter = action.payload;
    },
    setZeroLesson: (state, action: PayloadAction<LessonType>) => {
      state.zeroLesson = action.payload;
    },
    setEditableLesson: (state, action: PayloadAction<LessonType>) => {
      state.editableLesson = action.payload;
    },
    setIsTemplateLessons: (state, action: PayloadAction<boolean>) => {
      state.isTemplateLessons = action.payload;
    },
    setLesson: (state, action: PayloadAction<LessonType>) => {
      state.lesson = action.payload;
    },
    setDraggedLesson: (state, action: PayloadAction<LessonType>) => {
      state.draggedLesson = action.payload;
    },
    setZeroLessonTemplateLesson: (state, action: PayloadAction<LessonType>) => {
      state.zeroLessonTemplateLesson = action.payload;
    },

    setIsShowStudentLesson: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isShowStudentLesson = action.payload;
    },
    setIsInfoModalShowed: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isInfoModalShowed = action.payload;
    },
    setIsDateTimeExpired: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isDateTimeExpired = action.payload;
    },
    setToolsTab: (state, action: PayloadAction<number>) => {
      state.lessonCall.toolsTab = action.payload;
    },
    setIsRefetchUserGroupsData: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isRefetchUserGroupsData = action.payload;
    },
    setIsLessThan10MinutesLeft: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isLessThan10MinutesLeft = action.payload;
    },
    setPoorConnection: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.poorConnection = action.payload;
    },
    setReconnecting: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.reconnecting = action.payload;
    },
    setIsLessonLoading: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isLessonLoading = action.payload;
    },
    setIsAddExercisesToLesson: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isAddExercisesToLesson = action.payload;
    },
    setControlOwnerUserId: (state, action: PayloadAction<string | null>) => {
      state.lessonCall.controlOwnerUserId = action.payload;
    },
    setControlPendingFromUserId: (state, action: PayloadAction<string | null>) => {
      state.lessonCall.controlPendingFromUserId = action.payload;
    },
    setIsSetFullScreen: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isSetFullScreen = action.payload;
    },
    setIsStartLessonButtonShowed: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isStartLessonButtonShowed = action.payload;
    },
    setIsMounting: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isMounting = action.payload;
    },
    setIsShowAddLessonExercisesList: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isShowAddLessonExercisesList = action.payload;
    },
    setStudentFileSelectionFromTeacher: (state, action: PayloadAction<StudentFileSelectionType | null>) => {
      state.lessonCall.studentFileSelectionFromTeacher = action.payload;
    },
    setIsConnectionEstablished: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isConnectionEstablished = action.payload;
    },
    setIsRefetchLesson: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isRefetchLesson = action.payload;
    },
    setIsShowPromptCameraAccessBanner: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isShowPromptCameraAccessBanner = action.payload;
    },
    setMyVideoLoading: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.myVideoLoading = action.payload;
    },
    setIsCloseCall: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isCloseCall = action.payload;
    },
    setPeerIdToRemove: (state, action: PayloadAction<string | null>) => {
      state.lessonCall.peerIdToRemove = action.payload;
    },
    setIsUpdate: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isUpdate = action.payload;
    },
    setIsLessonEstablished: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isLessonEstablished = action.payload;
    },
    setIsSuperTeacherMode: (state, action: PayloadAction<boolean>) => {
      state.isSuperTeacherMode = action.payload;
    },
    setIsRefetchUsersData: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isRefetchUsersData = action.payload;
    },
    setIsMyVideoLoaded: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isMyVideoLoaded = action.payload;
    },
    setIsLessonComplete: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isLessonComplete = action.payload;
    },
    setFullScreen: (state, action: PayloadAction<string | null>) => {
      state.lessonCall.fullScreen = action.payload;
    },
    setDeviceId: (state, action: PayloadAction<string | null>) => {
      state.lessonCall.deviceId = action.payload;
    },
    setIsCalling: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isCalling = action.payload;
    },
    setIsMobileDevice: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isMobileDevice = action.payload;
    },
    setIsBasicModuleStructure: (state, action: PayloadAction<boolean>) => {
      state.isBasicModuleStructure = action.payload;
    },
    setRoomId: (state, action: PayloadAction<string | null>) => {
      state.lessonCall.roomId = action.payload;
    },
    setPeerId: (state, action: PayloadAction<string | null>) => {
      state.lessonCall.peerId = action.payload;
    },
    setVideoDevices: (state, action: PayloadAction<MediaDeviceInfo[]>) => {
      state.lessonCall.videoDevices = action.payload;
    },
    setMyVideoTrackId: (state, action: PayloadAction<string | null>) => {
      state.lessonCall.myVideoTrackId = action.payload;
    },
    setMyVideoTrack: (state, action: PayloadAction<MediaStreamTrack | null>) => {
      state.lessonCall.myVideoTrack = action.payload;
    },
    setIsCallStarted: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isCallStarted = action.payload;
    },
    setIsScreenSharing: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isScreenSharing = action.payload;
    },
    setMyVideoStream: (state, action: PayloadAction<MediaStream | null>) => {
      state.lessonCall.myVideoStream = action.payload;
    },
    setInitialLessonId: (state, action: PayloadAction<string | null>) => {
      state.lessonCall.initialLessonId = action.payload;
    },
    setIsShowNoCameraAccessAlertBanner: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isShowNoCameraAccessAlertBanner = action.payload;
    },
    setShowVideoDevices: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.showVideoDevices = action.payload;
    },
    setIsCameraUsedByAnotherApp: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isCameraUsedByAnotherApp = action.payload;
    },
    setOpenedToolsBeforeFullScreen: (state, action: PayloadAction<string | null>) => {
      state.lessonCall.openedToolsBeforeFullScreen = action.payload;
    },
    setIsSketchMode: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isSketchMode = action.payload;
    },
    setShowHeader: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.showHeader = action.payload;
    },
    setIsCallToolsCollapsed: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isCallToolsCollapsed = action.payload;
    },
    setLessonUsersList: (state, action: PayloadAction<LessonUserListItemType[]>) => {
      state.lessonCall.lessonUsersList = action.payload;
    },
    setIsShowStudentVideoStreams: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isShowStudentVideoStreams = action.payload;
    },
    setShowVideoTools: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.showVideoTools = action.payload;
    },
    setIsRecording: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isRecording = action.payload;
    },
    setIsLessonStatusInfo: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isLessonStatusInfo = action.payload;
    },
    setIsPreviewShowed: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isPreviewShowed = action.payload;
    },
    setFullScreenUserId: (state, action: PayloadAction<string | null>) => {
      state.lessonCall.fullScreenUserId = action.payload;
    },
    setIsStudentFullScreenMode: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isStudentFullScreenMode = action.payload;
    },
    setVideoMuted: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.videoMuted = action.payload;
    },
    setAudioMuted: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.audioMuted = action.payload;
    },
    setIsAccessToCameraMicDenied: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isAccessToCameraMicDenied = action.payload;
    },
    setSelectedExerciseIndex: (state, action: PayloadAction<number | null>) => {
      state.lessonCall.selectedExerciseIndex = action.payload;
    },
    setIsMyPeerShowed: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isMyPeerShowed = action.payload;
    },
    setIsShowVideoStreams: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isShowVideoStreams = action.payload;
    },
    setIsZeroLessonCategoryChosen: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isZeroLessonCategoryChosen = action.payload;
    },
    setIsCourseCategoryComplete: (state, action: PayloadAction<boolean>) => {
      state.isCourseCategoryComplete = action.payload;
    },
    setIsUnsetSelectedUser: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isUnsetSelectedUser = action.payload;
    },
    setEditableStudentId: (state, action: PayloadAction<string | null>) => {
      state.lessonCall.editableStudentId = action.payload;
    },
    setIsUpdateEditableHomeTaskForm: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isUpdateEditableHomeTaskForm = action.payload;
    },
    setIsLessonExercisesList: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isLessonExercisesList = action.payload;
    },
    setZeroLessonDateTime: (state, action: PayloadAction<string | null>) => {
      state.lessonCall.zeroLessonDateTime = action.payload;
    },
    setIsAllButtonsMode: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isAllButtonsMode = action.payload;
    },
    setIsCall: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isCall = action.payload;
    },
    setEditableHomeTaskLesson: (state, action: PayloadAction<LessonType>) => {
      state.lessonCall.editableHomeTaskLesson = action.payload;
    },
    setIsFullScreenMode: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isFullScreenMode = action.payload;
    },
    changeStateOfHomeTaskModal: (state, action: PayloadAction<HomeTaskModalType>) => {
      state.lessonCall.homeTaskModal = action.payload;
    },
    setIsMessagesList: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isMessagesList = action.payload;
    },
    setSelectedZeroLessonId: (state, action: PayloadAction<string | null>) => {
      state.lessonCall.selectedZeroLessonId = action.payload;
    },
    setIsUsersList: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isUsersList = action.payload;
    },
    setIsVideoConnectionEstablished: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isVideoConnectionEstablished = action.payload;
    },
    setShowExerciseForStudent: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.showExerciseForStudent = action.payload;
    },
    setSelectedZeroLessonCategoryId: (state, action: PayloadAction<string | null>) => {
      state.lessonCall.selectedZeroLessonCategoryId = action.payload;
    },
    setIsLessonResults: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isLessonResults = action.payload;
    },
    setIsCloseFullScreen: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isCloseFullScreen = action.payload;
    },

    setLessonTemplateLessonsPagination: (state, action: PayloadAction<PaginationType<Record<string, unknown>>>) => {
      state.lessonTemplateLessonsPagination = action.payload;
    },
    setLessonTemplateLessonsPaginationLoading: (state, action: PayloadAction<boolean>) => {
      state.lessonTemplateLessonsPagination.isLoading = action.payload;
    },
    appendLessonTemplateLessonsPagination: (
      state,
      action: PayloadAction<{
        items: Record<string, unknown>[];
        pageInfo: PageInfoType;
      }>,
    ) => {
      state.lessonTemplateLessonsPagination.items = [
        ...state.lessonTemplateLessonsPagination.items,
        ...(action.payload.items ?? []),
      ];
      state.lessonTemplateLessonsPagination.pageInfo = action.payload.pageInfo;
    },
    resetLessonTemplateLessonsPagination: (state) => {
      state.lessonTemplateLessonsPagination.items = [];
      state.lessonTemplateLessonsPagination.isLoading = false;
      state.lessonTemplateLessonsPagination.pageInfo = initialPageInfo;
    },

    setIsUpdateStudentLesson: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isUpdateStudentLesson = action.payload;
    },
    setSelectedExerciseWithFiles: (state, action: PayloadAction<FileType | null>) => {
      state.lessonCall.selectedExerciseWithFiles = action.payload;
    },
    setStudentFileSelection: (state, action: PayloadAction<StudentFileSelectionType | null>) => {
      state.lessonCall.studentFileSelection = action.payload;
    },
    setIsDocReaderShowed: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isDocReaderShowed = action.payload;
      state.isDocReaderShowed = action.payload;
    },
    setIsSaveLessonTemplatesMode: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.isSaveLessonTemplatesMode = action.payload;
    },

    setLessonsPagination: (
      state,
      action: PayloadAction<{
        items: Record<string, unknown>[];
        pageInfo: PageInfoType;
        isLoading?: boolean;
      }>,
    ) => {
      state.lessonsPagination = action.payload;
      /*    state.lessonsPagination = {
        items: action.payload.items,
        pageInfo: action.payload.pageInfo,
        isLoading: action.payload.isLoading ?? state.lessonsPagination.isLoading,
      };*/
    },
    setIsLessonsPaginationReset: (state, action: PayloadAction<boolean>) => {
      state.isLessonsPaginationReset = action.payload;
    },
    setLessonsPaginationLoading: (state, action: PayloadAction<boolean>) => {
      state.lessonsPagination.isLoading = action.payload;
    },
    setHasMyVideoStream: (state, action: PayloadAction<boolean>) => {
      state.lessonCall.hasMyVideoStream = action.payload;
    },
  },
});

export const {
  setLessonTemplateLessonsPaginationLoading,
  setFullScreenUserId,
  setIsShowNoCameraAccessAlertBanner,
  setHasMyVideoStream,
  setMyVideoTrackId,
  setMyVideoTrack,
  setLessonsPaginationLoading,
  setIsLessonsPaginationReset,
  setPeerId,
  setSelectedExerciseIndex,
  setIsCameraUsedByAnotherApp,
  setStudentFileSelectionFromTeacher,
  setIsCallToolsCollapsed,
  setShowVideoDevices,
  setFullScreen,
  setIsShowAddLessonExercisesList,
  setIsRefetchLesson,
  setRoomId,
  setIsCalling,
  setDeviceId,
  setIsSketchMode,
  setStudentsListTab,
  setIsLessonComplete,
  setShowHeader,
  setOpenedToolsBeforeFullScreen,
  setIsShowStudentVideoStreams,
  setIsLessonStatusInfo,
  setIsCloseCall,
  setIsRefetchUsersData,
  setIsMobileDevice,
  setIsFullScreenMode,
  setInitialLessonId,
  setIsEditableLessons,
  setIsScreenSharing,
  setIsUnsetSelectedUser,
  setCloneLessonMode,
  setControlOwnerUserId,
  appendLessonTemplateLessonsPagination,
  setIsRecording,
  setIsCallStarted,
  setIsShowVideoStreams,
  setLessonTemplateLessonsPaginationReset,
  setIsShowPromptCameraAccessBanner,
  setIsUpdateEditableHomeTaskForm,
  setIsZeroLessonCategoryChosen,
  setVideoDevices,
  setShowVideoTools,
  resetLessonTemplateLessonsPagination,
  setDraggedLesson,
  setIsStudentFullScreenMode,
  setVideoMuted,
  setSelectedExerciseWithFiles,
  setAudioMuted,
  setIsMounting,
  setIsMyPeerShowed,
  setAllowedParticipants,
  clearAllowedParticipants,
  setIsLessonEstablished,
  setIsSaveLessonTemplatesMode,
  setLessonUsersList,
  setZeroLessonTemplateLesson,
  setIsAccessToCameraMicDenied,
  setLessonTemplateLessonsPagination,
  setCloneLesson,
  setIsMessagesList,
  setPeerIdToRemove,
  setControlPendingFromUserId,
  setIsSuperTeacherMode,
  setEditableLesson,
  setIsLessonExercisesList,
  setIsAllButtonsMode,
  setIsVideoConnectionEstablished,
  setIsCall,
  setIsMyVideoLoaded,
  setIsUsersList,
  setSelectedZeroLessonCategoryId,
  setIsTemplateLessons,
  setMyVideoStream,
  setEditableStudentId,
  changeStateOfHomeTaskModal,
  setIsInfoModalShowed,
  setIsUpdate,
  setSelectedZeroLessonId,
  setEditableHomeTaskLesson,
  setZeroLessonDateTime,
  setShowExerciseForStudent,
  setIsStartLessonButtonShowed,
  setIsLessonResults,
  setMyVideoLoading,
  setIsCloseFullScreen,
  setToolsTab,
  setIsPreviewShowed,
  setIsLessThan10MinutesLeft,
  setIsAddExercisesToLesson,
  setIsDateTimeExpired,
  setIsSetFullScreen,
  setStudentFileSelection,
  setZeroLesson,
  setPoorConnection,
  setReconnecting,
  updatePeerUser,
  setCourseAlias,
  setIsRefetchUserGroupsData,
  setLessonsListFilter,
  setSelectedEventLesson,
  setIsConnectionEstablished,
  setIsLessonLoading,
  setIsUpdateStudentLesson,
  setIsDocReaderShowed,
  setCurrentBoardLesson,
  setCurrentBoardLessonIndex,
  setIsShowStudentLesson,
  setIsCourseCategoryComplete,
  setIsBasicModuleStructure,
  setLesson,
  setLessonsPagination,
} = lessonsSlice.actions;

export default lessonsSlice.reducer;
